export const owners = [
  {
    name: "Salesforce",
    logo: "/salesforce.png",
    category: "Strategic Cloud Partner",
    desc: "End-to-end Salesforce implementation, customization, and managed services across Sales Cloud, Service Cloud, Marketing Cloud, and Commerce Cloud.",
  },
  {
    name: "Sitecore",
    logo: "/sitecore.png",
    category: "Digital Experience Partner",
    desc: "Enterprise content management, personalization, and omnichannel digital experience delivery powered by Sitecore XP, XM Cloud, and CDP.",
  },
  {
    name: "Snowflake",
    logo: "/snowflake.png",
    category: "Data Cloud Partner",
    desc: "Data warehouse modernization, real-time analytics pipelines, and AI-ready data platform architecture on Snowflake's elastic cloud.",
  },
  {
    name: "Tableau",
    logo: "/tableau.png",
    category: "Analytics Partner",
    desc: "Interactive dashboards, self-service analytics, and embedded BI solutions that turn enterprise data into actionable business intelligence.",
  },
  {
    name: "Slack",
    logo: "/slack.png",
    category: "Collaboration Partner",
    desc: "Workflow automation, enterprise integrations, and intelligent collaboration solutions that connect teams and accelerate decision-making.",
  },
  {
    name: "Kognitos",
    logo: "https://www.kognitos.com/img/kognitos-logo.svg",
    category: "AI Automation Partner",
    desc: "Generative AI-powered business automation that enables enterprises to automate complex processes using natural language, eliminating the need for traditional coding.",
  },
]

export const PartnersSection = () => {
  return (
    <section id="partners" className="relative overflow-hidden">
      {/* Hero banner */}
      <div className="relative py-24 md:py-32 overflow-hidden bg-[#0f1629]">
        {/* Background layers */}
        <div className="absolute inset-0">
          <img
            src="/partners_page_hero_bg.png"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1629]/60 via-[#0f1629]/80 to-[#0f1629]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#1C2B6E]/15 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.06] rounded-full blur-[120px]" />
        </div>

        <div className="section-container relative z-10 text-center">
          <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
            Technology Alliances
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6">
            Strategic{" "}
            <span className="text-[#FDA400]">Partners</span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            We collaborate with world-class technology providers to deliver superior, scalable, and innovative outcomes for our clients.
          </p>

          {/* Logo bar */}
          <div className="mt-16 flex items-center justify-center gap-10 md:gap-16 flex-wrap">
            {owners.map((partner) => (
              <div key={partner.name} className="opacity-60 hover:opacity-100 transition-opacity duration-300">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-8 md:h-10 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partner showcase */}
      <div className="bg-gradient-to-b from-slate-50 via-white to-slate-50 py-20 md:py-28 relative overflow-hidden">
        {/* Subtle dot grid background */}
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "24px 24px",
          }}
        />

        {/* Decorative blurs */}
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          {/* Section label */}
          <div className="text-center mb-20">
            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              Our Ecosystem
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              Powering Enterprise{" "}
              <span className="text-[#FDA400]">Transformation</span>
            </h2>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card 1: Salesforce - Featured large card */}
            <div className="partner-card group rounded-3xl overflow-hidden lg:col-span-2 lg:row-span-2 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.15)] transition-all duration-500 hover:-translate-y-2"
              style={{ perspective: "1000px" }}
            >
              {/* Shine sweep on hover */}
              <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
              </div>

              {/* Top accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] via-[#f5b800] to-[#FDA400] z-10" />

              {/* Corner glow orbs */}
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#FDA400]/[0.05] rounded-full blur-[80px] group-hover:bg-[#FDA400]/[0.12] group-hover:scale-110 transition-all duration-700" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[#1C2B6E]/[0.04] rounded-full blur-[60px] group-hover:bg-[#1C2B6E]/[0.1] transition-all duration-700" />

              <span className="absolute top-6 right-8 text-[140px] font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.06] transition-colors duration-500">01</span>

              <div className="p-8 md:p-12 lg:p-14 h-full flex flex-col relative z-10">
                <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.2em] bg-[#FDA400]/10 border border-[#FDA400]/20 px-4 py-2 rounded-full self-start mb-10 backdrop-blur-sm">
                  {owners[0].category}
                </span>

                <div className="flex-1 flex flex-col justify-center">
                  {/* Logo with animated glow */}
                  <div className="mb-10 relative inline-block self-start">
                    <div className="absolute inset-0 bg-[#FDA400]/10 blur-[35px] rounded-full scale-[2] opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    <img
                      src={owners[0].logo}
                      alt={owners[0].name}
                      className="h-16 md:h-20 w-auto object-contain relative z-10 group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  <h3 className="text-3xl md:text-5xl font-black text-[#1C2B6E] tracking-tight mb-5">
                    {owners[0].name}
                  </h3>

                  <p className="text-slate-500 text-lg leading-relaxed font-light max-w-xl mb-10">
                    {owners[0].desc}
                  </p>

                  <div className="flex items-center gap-3 flex-wrap pt-6 border-t border-slate-200/60">
                    {["Sales Cloud", "Service Cloud", "Marketing Cloud", "Commerce Cloud"].map((cloud) => (
                      <span key={cloud} className="text-[10px] text-[#1C2B6E]/60 uppercase tracking-wider font-semibold bg-[#1C2B6E]/[0.04] px-4 py-2 rounded-full border border-[#1C2B6E]/[0.08] backdrop-blur-sm hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default">
                        {cloud}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Cards 2-5 */}
            {owners.slice(1).map((partner, index) => (
              <div
                key={index}
                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
              >
                {/* Shine sweep */}
                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                  <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                </div>

                {/* Top accent - animated reveal */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-600 origin-left z-10" />

                {/* Glow orb */}
                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                <span className="absolute top-4 right-5 text-8xl font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.07] transition-colors duration-500">
                  {String(index + 2).padStart(2, "0")}
                </span>

                <div className="p-7 md:p-9 h-full flex flex-col relative z-10">
                  <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.2em] bg-[#FDA400]/10 border border-[#FDA400]/20 px-4 py-2 rounded-full self-start mb-6 backdrop-blur-sm">
                    {partner.category}
                  </span>

                  {/* Logo with glow */}
                  <div className="mb-6 relative inline-block self-start">
                    <div className="absolute inset-0 bg-[#FDA400]/[0.08] blur-[25px] rounded-full scale-[2] opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="h-12 w-auto object-contain relative z-10 group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  <h3 className="text-2xl md:text-2xl font-black text-[#1C2B6E] tracking-tight mb-3">
                    {partner.name}
                  </h3>

                  <p className="text-slate-500 text-sm leading-relaxed font-light flex-1">
                    {partner.desc}
                  </p>

                  <div className="mt-6 pt-5 border-t border-slate-200/60">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-40" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                      </span>
                      <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                        Certified Partnership
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
